import { Injectable } from '@angular/core';
import { inflate } from 'zlib';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../environments/environment';
import { AuthenticationService } from './authentication.service';
import { CredentialService } from './credential.service';
import { TranslateService } from '../i18n';

import {
  Job,
  Order,
  Shipment
} from '../models';

import {
  allJobFields,
  allProductFields,
  allFullOrderFields,
  allOrderFields,
  allInvalidJobScan,
  allHoldBinFields
} from '../../ngrx/graphql';

import { OA, RFID, SHEETS } from '../../ngrx/category-types';

import {
  QUEUED,
  HOLD,
  PRINT_READY,
  SHIPMENTS,
  statusTypes,
  CANCELLED,
  COMMENT,
  PENDING_PRINTING,
  RELEASE_CS,
  UNCANCELLED,
  oaStatusTypes,
  PREPRESS,
  PREPRINT,
  rfidStatusTypes,
  COLLATING
} from '../../ngrx/status-types';
import { holdAmountsReducer } from 'ngrx/reducers/holds';
import { keyframes } from '@angular/animations';
import { BoundCallbackObservable } from 'rxjs/observable/BoundCallbackObservable';

const errorCodes = [400, 401, 403, 404];

@Injectable()
export class OrderService {
  earliestOrderStatus: string;
  shipment           : Shipment;
  updatedJob         : Job;
  updatedOrder       : Order;
  getArchivedOrder   : Subject<any> = new Subject();
  openPackingModal   : Subject<any> = new Subject();

  constructor(
    private authService      : AuthenticationService,
    private credentialService: CredentialService,
    private store            : Store<any>,
    private translate        : TranslateService
  ) {}

  private invokeOrders(payload: any): Observable<any> {
    return new Observable(observer => {

      this.credentialService.lambda.invoke({
        FunctionName: environment.magentoFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            observer.error(err.stack);
          } else {
              const output = typeof data.Payload === 'string'
                ? JSON.parse(data.Payload)
                :       data.Payload;
              if(output.gzip){
                const options = {
                  windowBits : 15
                };
                inflate(new Buffer(output.body,'base64'), options, (err, data) => {
                  if (err) {                    
                    observer.error(err.stack);
                  } else {
                    const unzippedOutput = Object.assign({}, output, {
                      body: data.toString()
                    });
                    observer.next(unzippedOutput);
                  }
                });
              } else {
                observer.next(output);
              }
          }
        }
      );
    });
  }

  private invokeOrderMutation(payload: any): void {
    this.credentialService.lambda.invoke({
      FunctionName: environment.magentoFunction,
      Payload     : JSON.stringify(payload)
      }, (err, data) => {
        if (err) {
          console.log(err)
          if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
            this.authService.refreshCredentials();
              // this.authService.logout();
          }
        }
      }
    );
  }

  regenLaserFiles(payload: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.credentialService.lambda.invoke({
        FunctionName: environment.laserRegenarateFilesFunction,
        Payload     : JSON.stringify(payload)
        }, (err, data) => {
          if (err) {
            console.log(err)
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
                // this.authService.logout();
            }
            resolve(false);
          } else {
            console.log(`Laser Files have been renerated : ${JSON.stringify(data)}`);
            resolve(true);
          }
        }
      );
    });
  }

  async regenRFIDLaserFiles(type: string, outsource: any, payload: any): Promise<boolean> {
    if (type !== 'innercase') {
      console.warn(`Unsupported type: ${type}`);
      return false; // Handle unsupported type gracefully
    }
  
    try {
      const result = await this.invokeLambdaFunction(environment.innercaseRegenerateFilesFunction, { outsource: outsource, job: payload });
      console.log(`Laser Files have been regenerated: ${JSON.stringify(result)}`);
      return true;
    } catch (error) {
      console.error('Error regenerating laser files:', error);
      return false;
    }
  }

  async generateBoxLabels(payload: any): Promise<any> {
    try {
      const result = await this.invokeLambdaFunction(environment.generateBoxLabelsFunction, { packages: payload });
      console.log(`Box labels have been generated: ${JSON.stringify(result)}`);
      
      // Parse and return the response body
      return JSON.parse(result.Payload).body;
    } catch (error) {
      console.error('Error generating box labels:', error);
      throw error;
    }
  }
  
  private invokeLambdaFunction(functionName: string, payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.credentialService.lambda.invoke(
        {
          FunctionName: functionName,
          Payload: JSON.stringify(payload),
        },
        (err, data) => {
          if (err) {
            reject(err);
          } else {
            resolve(data);
          }
        }
      );
    });
  }


  allJobsReadyToShip(jobs: Job[]): boolean {
    return jobs.every(job => job.status === SHIPMENTS);
  }

  formatJob(job: any): Job {
    const formattedJob = Object.assign({}, job, {
      product: job.product.sku
    })

    if (job.hasOwnProperty('daysUntilDue')) delete formattedJob.daysUntilDue;
    if (job.hasOwnProperty('ttl')) delete formattedJob.ttl;
    if (job.hasOwnProperty('daysUntilDueMsg')) delete formattedJob.daysUntilDueMsg;
    if (job.hasOwnProperty('specs.shape')) delete formattedJob['specs.shape'];
    if (job.hasOwnProperty('specs.width')) delete formattedJob['specs.width'];
    if (job.hasOwnProperty('specs.height')) delete formattedJob['specs.height'];
    if (job.hasOwnProperty('hasComment')) delete formattedJob.hasComment;
    if (job.hasOwnProperty('note')) delete formattedJob.note;
    if (job.hasOwnProperty('comment')) delete formattedJob.comment;
    if (job.hasOwnProperty('oaDetails.template.laser_enabled')) delete formattedJob['oaDetails.template.laser_enabled'];
    if (job.hasOwnProperty('oaDetails.template.project_size_description')) delete formattedJob['oaDetails.template.project_size_description'];
    if (job.hasOwnProperty('oaDetails.template.project_stencil_id')) delete formattedJob['oaDetails.template.project_stencil_id'];
    if (job.hasOwnProperty('oaDetails.template.project_template_id')) delete formattedJob['oaDetails.template.project_template_id'];
    if (job.hasOwnProperty('oaDetails.template.project_snapshot_id')) delete formattedJob['oaDetails.template.project_snapshot_id'];
    if (job.hasOwnProperty('oaDetails.template.image_code')) delete formattedJob['oaDetails.template.image_code'];
    if (job.hasOwnProperty('oaDetails.template.cutting_pattern_pdf_url')) delete formattedJob['oaDetails.template.cutting_pattern_pdf_url'];
    if (job.hasOwnProperty('oaDetails.template.is_custom_size')) delete formattedJob['oaDetails.template.is_custom_size'];
    if (job.hasOwnProperty('oaDetails.material.laser_enabled')) delete formattedJob['oaDetails.material.laser_enabled'];
    if (job.hasOwnProperty('oaDetails.material.country_origin')) delete formattedJob['oaDetails.material.country_origin'];
    if (job.hasOwnProperty('oaDetails.material.coo_code')) delete formattedJob['oaDetails.material.coo_code'];
    if (job.hasOwnProperty('oaDetails.pto')) delete formattedJob['oaDetails.pto'];
    if (job.hasOwnProperty('batchIdHistory')) delete formattedJob['batchIdHistory'];

    // if (job.hasOwnProperty('reprint')) delete formattedJob.reprint;

    return formattedJob;
  }

  formatOrder(order: any): Order {
    const formattedOrder = Object.assign({}, order);

    if (order.hasOwnProperty('name')) delete formattedOrder.name;
    if (order.hasOwnProperty('ttl')) delete formattedOrder.ttl;
    if (order.hasOwnProperty('daysUntilDue')) delete formattedOrder.daysUntilDue;
    if (order.hasOwnProperty('daysUntilDueMsg')) delete formattedOrder.daysUntilDueMsg;
    if (order.hasOwnProperty('hasShippingLabel')) delete formattedOrder.hasShippingLabel;
    if (order.hasOwnProperty('diffNBD')) delete formattedOrder.diffNBD;
    if (!order.hasOwnProperty('weight') || order.weight === null) formattedOrder.weight = 0;
    if (order.hasOwnProperty('hasComment')) delete formattedOrder.hasComment;
    if (order.hasOwnProperty('marketplace')) delete formattedOrder.marketplace;
    // if (order.hasOwnProperty('reprint')) delete formattedOrder.reprint;

    return formattedOrder;
  }

  getAll(category): Observable<any> {
    const query = `{
      activeJobs(category: "${category}") {
        ${allJobFields}
      }

      activeOrders(category: "${category}") {
        ${allOrderFields}
      }

      products(param: "all") {
        ${allProductFields}
      }

      holdBins(param: "category", value: "${category}") {
        ${allHoldBinFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getFullOrder(id: string): Observable<any> {
    const query = `{
      fullOrder(id: "${id}") {
        ${allFullOrderFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getOrder(id: string): Observable<any> {
    const query = `{
      order(id: "${id}") {
        ${allOrderFields}
      }
    }`;
    
    return this.invokeOrders({ query });
  }

  getCurrentlyDueOrders(): Observable<any> {
    return this.store.select('orders', 'currentlyDueOrders');
  }

  getDueTodayOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'dueTodayOrderList');
  }

  getDueTomorrowOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'dueTomorrowOrderList');
  }

  getMailInnovationsOrders(): Observable<Order[]> {
    return this.store.select('orders', 'mailInnovationsOrderList');
  }

  getNormalOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'normalOrderList');
  }
  
  getOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'orderList');
  }

  getCommentList(): Observable<any> {
    return this.store.select('orders', 'orderCommentsMap');
  }

  getOverdueOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'overdueOrderList');
  }

  getReprintOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'reprintOrderList');
  }

  getReprintOverdueOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'reprintOverdueOrderList');
  }

  getRTSOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'RTSOrderList');
  }

  getPendingPrintOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'pendingPrintOrderList');
  }

  getPrePressOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'prePressOrderList');
  }

  getAddressInvalidOrderList(): Observable<Order[]> {
    return this.store.select('orders', 'addressInvalidOrderList');
  }
  
  getFullOrderByJobID(id: string): Observable<any> {
    const query = `{
      fullOrderByJobID(id: "${id}") {
        ${allFullOrderFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getOrderByJobID(id: string): Observable<any> {
    const query = `{
      orderByJobID(id: "${id}") {
        ${allOrderFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getOrdersByID(ids: string[]): Observable<any> {
    let formattedIDs = '';
    ids.forEach(id => {
      formattedIDs += `"${id}", `;
    });

    const query = `{
      ordersByID(ids: [${formattedIDs.trim()}]) {
        ${allOrderFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getProducts(): Observable<any> {
    const query = `{
      products(param: "all") {
        ${allProductFields}
      }
    }`;

    return this.invokeOrders({ query });
  }

  getEarliestOrderStatus(jobs: Job[], process = 'sheets'): string {
    let earliestOrderStatus = statusTypes.length;
    let sheetStatusTypes = [...statusTypes, PREPRESS];
    jobs.forEach(job => {
      const jobStatus = process === SHEETS ? sheetStatusTypes.indexOf(job.status) : 
                        process === OA ? oaStatusTypes.indexOf(job.status) : 
                        process === RFID ? rfidStatusTypes.indexOf(job.status) : 0;

      if (jobStatus < earliestOrderStatus) {
        earliestOrderStatus = jobStatus;
      }
    });
    const finalStatus = process === SHEETS 
      ? sheetStatusTypes[earliestOrderStatus] 
      : oaStatusTypes[earliestOrderStatus];
    return finalStatus;
  }

  updateFullOrderStatus(order: Order, jobs: Job[], status: string, reason?: string, reprint?: boolean, comment?: string): void {
    // Update status of an entire order
    const user             = JSON.parse(localStorage.getItem('currentUser'));
    if (!user) {
      this.authService.logout();
    }

    const timestamp        = new Date().getTime().toString();
    const activity_history = order.activity_history ? [ ...order.activity_history ] : [];
    const updatedOrder     = Object.assign({}, order);
    let updatedJobs      = jobs
      .map(job => this.updateJobStatus(job, status, reason, reprint, comment))

    if (status === HOLD && reason) {
      updatedJobs = updatedJobs.filter(job => job.status === status && (job.status === HOLD && job.previous_status !== PRINT_READY));
      if (order.status !== HOLD) {
        updatedOrder.previous_status = order.status;
      }
      updatedOrder.status          = HOLD;

      activity_history.unshift({
        activity: HOLD,
        note    : reason,
        timestamp,
        user: user.email,
        comment
      });

      updatedOrder.activity_history = activity_history;
    } else if (status === COMMENT) {
      activity_history.unshift({
        activity: COMMENT,
        note    : reason,
        comment,
        timestamp,
        user: user.email
      });

      updatedOrder.activity_history = activity_history;
    } else if (status === PENDING_PRINTING) {
      updatedOrder.previous_status = order.status;
      updatedOrder.status          = PENDING_PRINTING;

      activity_history.unshift({
        activity: PENDING_PRINTING,
        note    : reason,
        comment,
        timestamp,
        user: user.email
      });

      updatedOrder.activity_history = activity_history;
    }else if (reprint) {
      updatedJobs = updatedJobs.filter(job => job.status === status);
      updatedOrder.status          = QUEUED;
      updatedOrder.previous_status = status;

      activity_history.unshift({
        activity: QUEUED,
        note    : reason,
        timestamp,
        user: user.email
      });

      updatedOrder.activity_history = activity_history;
    } else if (status === UNCANCELLED || status === RELEASE_CS) {
      
      updatedOrder.status          = updatedOrder.previous_status;
      updatedOrder.previous_status = CANCELLED;

      activity_history.unshift({
        activity: updatedOrder.status ,
        note    : status === UNCANCELLED ? "Order un-cancelled" : "Order released from pending printing",
        timestamp,
        user: user.email
      });

      updatedOrder.activity_history = activity_history;
    }else if (status !== order.status) {
      const status                       = this.getEarliestOrderStatus(updatedJobs, order.category);
            updatedOrder.status          = status !== CANCELLED ? status : QUEUED;
            updatedOrder.previous_status = order.status;

      activity_history.unshift({
        activity: status,
        note    : reason,
        timestamp,
        user: user.email
      });

      updatedOrder.activity_history = activity_history;
    }  

    this.updateOrderAndJobs(updatedOrder, updatedJobs);
  }

  setJobStatus(job: Job, status: string, reason?: string, reprint?: boolean, comment?: string): void {
    console.log(`setting ${job.category} job status to "${status}"`);

    const updatedJob = this.updateJobStatus(job, status, reason, reprint, comment);
    
    this.updateJob(updatedJob);
  }

  setJobStatuses(jobs: Job[], status: string, reason?: string, reprint?: boolean, comment?: string): void {
    console.log(`setting entire order status to "${status}"`);
    const updatedJobs = jobs.map(job => {
      let newReason = reason;
      if(reprint) {
        newReason = '| ' + job.numPages + ' ' + reason;
      }
      return this.updateJobStatus(job, status, newReason, reprint, comment)
    });

    this.updateJobs(updatedJobs);
  }

  resumeOrder(jobs: Job[]): void {
    const onHoldJobs = jobs.filter(job => job.status === HOLD && !!job.previous_status);

    if (onHoldJobs.length) {
      const updatedJobs = onHoldJobs.map(job => this.updateJobStatus(job, job.previous_status));
      this.updateJobs(updatedJobs);
    }
  }

  // updateOrderStatus(order: Order, jobs: Job[], status: string, reason?: string, reprint?: boolean): void {
  //   // Sync the current order status with each job update
  //   const user = JSON.parse(localStorage.getItem('currentUser'));
  //   const timestamp = new Date().getTime().toString();

  //   const activity_history = order.activity_history ? [ ...order.activity_history ] : [];
  //   const updatedJobs = jobs
  //     .map(job => this.updateJobStatus(job, status, reason, reprint))
  //     .filter(job => job.status === status);
  //   const updatedOrder = Object.assign({}, order, {
  //     jobs: [ ...order.jobs ]
  //   });

  //   updatedJobs.forEach(job => {
  //     // Replace currently existing jobs with their updated version
  //     const jobToUpdate = updatedOrder.jobs.findIndex(orderJob => orderJob.id === job.id);
  //     updatedOrder.jobs.splice(jobToUpdate, 1, job);
  //   });

  //   const newStatus = this.getEarliestOrderStatus(updatedOrder.jobs);

  //   if (newStatus !== order.status) {
  //     // Order status should only change if the new order status is different from the existing one
  //     // Update order and job
  //     updatedOrder.previous_status = order.status;
  //     updatedOrder.status = newStatus;

  //     activity_history.unshift({
  //       activity: newStatus,
  //       note    : reason ? reason : this.translate.instant('ORDER_STATUS_UPDATED'),
  //       timestamp,
  //       user: user.email
  //     });

  //     updatedOrder.activity_history = activity_history;
  //   }

  //   this.updateOrderAndJobs(updatedOrder, updatedJobs);
  // }

  private updateJobStatus(job: Job, status: string, reason?: string, reprint?: boolean, comment?: string): Job {
    // Update status of a single job
    const user = JSON.parse(localStorage.getItem('currentUser'));
    // console.log('user');
    // console.log(user);
    if(!user) {
      this.authService.logout();
    } 
    const timestamp        = new Date().getTime().toString();
    const updatedJob       = Object.assign({}, job);
    const activity_history = job.activity_history ? [ ...job.activity_history ] : [];

    if (status === QUEUED && job.status === QUEUED && reason) {
      console.log(`Skipping reprint for Job ${job.id}, batch is already queued`);
      return updatedJob;
    }
    
    if(status === COMMENT) {
      activity_history.unshift({
        activity: COMMENT,
        note    : reason,
        comment,
        timestamp,
        user: user.email
      });

      updatedJob.activity_history = activity_history;
    } else if (status === HOLD && reason) {
      console.log(`Hold job ${job.id}`);

      let batchIdHistory = job.batchIdHistory || [];
      if (job.batchId && !batchIdHistory.find(id => id === job.batchId)) {
        batchIdHistory.unshift(job.batchId);
      }

      updatedJob.batchId = null;
      updatedJob.batchIdHistory = batchIdHistory;
      
      if (job.status !== HOLD) {
        updatedJob.previous_status = job.status;
        updatedJob.status = HOLD;
      }

      //HOLD BIN REMOVAL
      //changing hold reason if assign hold bin not checked off
      if( job.holdBinId ){
        if(!comment || (comment && !(comment.includes('HOLD BIN')) ) ){
          updatedJob.holdBinId = null;
          activity_history.unshift({
            activity: 'HOLD BIN',
            note    : `Remove Hold Bin #${job.holdBinId}`,
            timestamp,
            user: user.email
          });
        }
      }
      
      activity_history.unshift({
        activity: HOLD,
        note    : reason,
        comment,
        timestamp,
        user: user.email
      });

      updatedJob.activity_history = activity_history;
    } else if (reprint) {
      if (job.category === OA) {
        console.log(`Recut job ${job.id}`);
      } else {
        console.log(`Reprint job ${job.id}`);
      }
      let recutStatus = QUEUED;
      updatedJob.previous_status = job.status;
      updatedJob.status          = QUEUED;
      updatedJob.reprint         = true;
      updatedJob.reprintCount    = job.reprintCount ? job.reprintCount + 1 : 1;
      const totalPages = updatedJob.reprint_pages && updatedJob.reprint_pages[0] && updatedJob.reprint_pages[0].totalPages 
                        ? updatedJob.reprint_pages && updatedJob.reprint_pages[0] && updatedJob.reprint_pages[0].totalPages 
                        : null;
      
      if(updatedJob.oaDetails && updatedJob.oaDetails.pto){ 
        updatedJob.status = PREPRINT; 
        recutStatus = PREPRINT;
      }
      activity_history.unshift({
        activity: recutStatus,
        note    : ` Reprint ${updatedJob.reprintCount} ( ${reason} ${comment} ) ${totalPages != null ? '' : ''}` || `Reprint ${updatedJob.reprintCount} ${totalPages != null ? '' : ''}`, // Don't remove 'Reprint' from the notes. We will be showing the line with a different color based on that.
        timestamp,
        user: user.email
      });

      updatedJob.activity_history = activity_history;


    } else if (status === UNCANCELLED || status === RELEASE_CS) {
      updatedJob.status = job.previous_status ? job.previous_status : QUEUED;
      updatedJob.previous_status = status === UNCANCELLED ? CANCELLED : PENDING_PRINTING;
      activity_history.unshift({
        activity: updatedJob.status,
        note    : status === UNCANCELLED ? "Order un-cancelled" : "Pending Print release by CS",
        timestamp,
        user: user.email
      });

      updatedJob.activity_history = activity_history;
    } else if (status !== job.status) {
      console.log(`Set job ${job.id}`);
            
      if (status !== QUEUED && status !== PRINT_READY && job.batchId) {
        console.log(`Removing batch id from job: ${job.batchId}`);

        let batchIdHistory = job.batchIdHistory || [];
        if (job.batchId && !batchIdHistory.find(id => id === job.batchId)) {
          batchIdHistory.unshift(job.batchId);
        }

        updatedJob.batchId = null;
        updatedJob.batchIdHistory = batchIdHistory;
      }

      if (job.status === HOLD && reason === "Order Resumed") {
        updatedJob.status = job.previous_status && job.previous_status !== CANCELLED? job.previous_status : QUEUED;
        updatedJob.previous_status = HOLD;
      } else {
        updatedJob.previous_status = job.status;
        updatedJob.status          = status;
      }

      //HOLD BIN REMOVAL
      if(job.holdBinId && job.status === HOLD){
        updatedJob.holdBinId = null;
        activity_history.unshift({
          activity: 'HOLD BIN',
          note    : `Remove Hold Bin #${job.holdBinId}`,
          timestamp,
          user: user.email
        });
      }
      
      activity_history.unshift({
        activity: updatedJob.status,
        note    : reason || this.translate.instant('JOB_STATUS_UPDATED'),
        timestamp,
        user: user.email
      });
      updatedJob.activity_history = activity_history;
    } 
    return updatedJob;
  }

  updateOrderAndJobs(order: any, jobs: any[]): void {
    const formattedOrder = this.formatOrder(order);
    const formattedJobs  = jobs.map(job => this.formatJob(job));

    console.log(`updating order: ${JSON.stringify(formattedOrder, null, 2)}`);
    console.log(`updating jobs: ${JSON.stringify(formattedJobs, null, 2)}`);

    if (order && jobs && jobs.length) {
      const query = `mutation($order: OrderInput!, $jobs: [JobInput]) {
        updateOrder(order: $order) {
          ${allOrderFields}
        }

        updateJobs(jobs: $jobs) {
          ${allJobFields}
        }
      }`;

      const variables = { jobs: formattedJobs, order: formattedOrder };

      // console.log('invoke update order and jobs');
      this.invokeOrderMutation({ query, variables });
    }
  }

  updateOrdersAndJobs(orders: any[], jobs: any[]): void {
    const formattedOrders = orders.map(order => this.formatOrder(order));
    const formattedJobs   = jobs.map(job => this.formatJob(job));

    console.log(`updating orders: ${JSON.stringify(formattedOrders, null, 2)}`);
    console.log(`updating jobs: ${JSON.stringify(formattedJobs, null, 2)}`);

    if (orders && orders.length && jobs && jobs.length) {
      const query = `mutation($orders: [OrderInput], $jobs: [JobInput]) {
        updateOrders(orders: $orders) {
          ${allOrderFields}
        }

        updateJobs(jobs: $jobs) {
          ${allJobFields}
        }
      }`;

      const variables = { jobs: formattedJobs, orders: formattedOrders };

      // console.log('invoke update orders and jobs');
      this.invokeOrderMutation({ query, variables });
    }
  }

  updateOrder(order: any): void {
    console.log(`updating order: ${JSON.stringify(order, null, 2)}`);
    if (order) {
      const formattedOrder = this.formatOrder(order);
      const query = `mutation($order: OrderInput!) {
        updateOrder(order: $order) {
          ${allOrderFields}
        }
      }`;

      const variables = { order: formattedOrder };

      // console.log('invoke update order');
      this.invokeOrderMutation({ query, variables });
    }
  }

  updateJob(job: any): void {
    if (job) {
      const formattedJob = this.formatJob(job);

      const query = `mutation($job: JobInput!) {
        updateJob(job: $job) {
          ${allJobFields}
        }
      }`;

      const variables = { job: formattedJob };

      console.log(`updating job: ${JSON.stringify(formattedJob, null, 2)}`);

      this.invokeOrderMutation({ query, variables });
    }
  }

  updateJobAsync(job: any): Promise<any> {
    return new Promise((resolve, reject) => {
      if (job) {
        const formattedJob = this.formatJob(job);

        const query = `mutation($job: JobInput!) {
          updateJob(job: $job) {
            ${allJobFields}
          }
        }`;

        const variables = { job: formattedJob };

        console.log(`updating job async: ${JSON.stringify(formattedJob, null, 2)}`);

        this.credentialService.lambda.invoke({
          FunctionName: environment.magentoFunction,
          Payload     : JSON.stringify({ query, variables })
        }, (err, data) => {
          if (err) {
            console.log(err);
            if (errorCodes.find(code => code === err.statusCode) && localStorage.getItem('currentUser')) {
              this.authService.refreshCredentials();
            }
            reject(err);
          } else {
            const output = typeof data.Payload === 'string'
              ? JSON.parse(data.Payload)
              : data.Payload;
            
            if (output.gzip) {
              const options = {
                windowBits: 15
              };
              inflate(new Buffer(output.body, 'base64'), options, (err, data) => {
                if (err) {
                  reject(err);
                } else {
                  const unzippedOutput = Object.assign({}, output, {
                    body: data.toString()
                  });
                  resolve(unzippedOutput);
                }
              });
            } else {
              resolve(output);
            }
          }
        });
      } else {
        reject(new Error('No job provided'));
      }
    });
  }

  updateJobs(jobs: any[]): void {
    if (jobs && jobs.length) {
      const formattedJobs = jobs.map(job => this.formatJob(job));

      const query = `mutation($jobs: [JobInput]) {
        updateJobs(jobs: $jobs) {
          ${allJobFields}
        }
      }`;

      const variables = { jobs: formattedJobs };

      console.log(`updating jobs: ${JSON.stringify(formattedJobs, null, 2)}`);

      this.invokeOrderMutation({ query, variables });
    }
  }

  createInvalidJobScan(invalidJobScan: any): void {
    console.log(`Creating invalidJobScan: ${JSON.stringify(invalidJobScan, null, 2)}`);
    if (invalidJobScan) {
      const query = `mutation($invalidJobScan: InvalidJobScanInput!) {
        createInvalidJobScan(invalidJobScan: $invalidJobScan) {
          ${allInvalidJobScan}
        }
      }`;

      const variables = { invalidJobScan: invalidJobScan };

      // console.log('invoke update order');
      this.invokeOrderMutation({ query, variables });
    }
  }
}


// WEBPACK FOOTER //
// /Users/itu/Desktop/impreso_updated_sheets/impresocloud/www/client/src/app/services/order.service.ts